import apiClient from './apiClient';
import { patientService } from '../modules/patient/services/patientService';

class PatientAnalyticsService {
  /**
   * Fetch analytics data for a patient by processing their appointment history
   * @param {string} patientId - The patient's ID
   * @returns {Promise<Object>} Analytics data grouped by vital sign type
   */
  async getPatientAnalytics(patientId) {
    try {
      console.log(`📊 Fetching analytics data for patient: ${patientId}`);
      
      // First, get the patient data to extract the numeric ID
      const patientData = await patientService.getPatientByPatientId(patientId);
      const numericPatientId = patientData.id;
      
      console.log(`🔍 Using numeric patient ID: ${numericPatientId} for analytics`);
      
      // Fetch patient history data which contains vitals
      const response = await apiClient.get(`/patient-history-test/patients/${numericPatientId}/history`);
      
      if (!response.data.success || !response.data.appointments) {
        throw new Error('Invalid response format from server');
      }
      
      const appointments = response.data.appointments;
      console.log(`📈 Processing ${appointments.length} appointments for analytics`);
      
      // Process the appointments to extract vitals data over time
      const analyticsData = this.processAppointmentsForAnalytics(appointments);
      
      console.log('✅ Analytics data processed successfully:', analyticsData);
      return analyticsData;
      
    } catch (error) {
      console.error('❌ Error fetching patient analytics:', error);
      throw new Error(this.getErrorMessage(error));
    }
  }

  /**
   * Process appointment data to create analytics datasets
   * @param {Array} appointments - Array of appointment objects
   * @returns {Object} Processed analytics data
   */
  processAppointmentsForAnalytics(appointments) {
    // Sort appointments by date (oldest first for timeline)
    const sortedAppointments = appointments.sort((a, b) => 
      new Date(a.booking.appointment_date) - new Date(b.booking.appointment_date)
    );

    // Initialize analytics data structure
    const analytics = {
      temperature: [],
      pulseRate: [],
      spo2: [],
      bloodPressure: [],
      respiratoryRate: [],
      glucose: [],
      weight: [],
      bmi: [],
      summary: {
        totalAppointments: appointments.length,
        dateRange: {
          start: null,
          end: null
        },
        latestVitals: null
      }
    };

    // Process each appointment's vitals
    sortedAppointments.forEach((appointment, index) => {
      const { booking, vitals } = appointment;
      const appointmentDate = new Date(booking.appointment_date);
      const formattedDate = this.formatDateForChart(appointmentDate);
      
      // Set date range
      if (index === 0) analytics.summary.dateRange.start = appointmentDate;
      if (index === sortedAppointments.length - 1) {
        analytics.summary.dateRange.end = appointmentDate;
        analytics.summary.latestVitals = vitals;
      }
      
      // Process vitals if available
      if (vitals) {
        // Temperature
        if (vitals.temperature !== null && vitals.temperature !== undefined) {
          analytics.temperature.push({
            date: formattedDate,
            temp: parseFloat(vitals.temperature),
            normal: 37.0,
            appointmentDate: booking.appointment_date,
            unit: vitals.temperature_unit || '°C'
          });
        }
        
        // Pulse Rate
        if (vitals.pulse_rate !== null && vitals.pulse_rate !== undefined) {
          analytics.pulseRate.push({
            date: formattedDate,
            rate: vitals.pulse_rate,
            target: 75,
            appointmentDate: booking.appointment_date
          });
        }
        
        // SpO2
        if (vitals.spo2 !== null && vitals.spo2 !== undefined) {
          analytics.spo2.push({
            date: formattedDate,
            spo2: vitals.spo2,
            normal: 95,
            appointmentDate: booking.appointment_date
          });
        }
        
        // Blood Pressure
        if (vitals.systolic_bp !== null && vitals.diastolic_bp !== null) {
          analytics.bloodPressure.push({
            date: formattedDate,
            systolic: vitals.systolic_bp,
            diastolic: vitals.diastolic_bp,
            normalSys: 120,
            normalDia: 80,
            appointmentDate: booking.appointment_date
          });
        }
        
        // Respiratory Rate
        if (vitals.respiratory_rate !== null && vitals.respiratory_rate !== undefined) {
          analytics.respiratoryRate.push({
            date: formattedDate,
            rate: vitals.respiratory_rate,
            normal: 16,
            appointmentDate: booking.appointment_date
          });
        }
        
        // Glucose
        if (vitals.glucose !== null && vitals.glucose !== undefined) {
          analytics.glucose.push({
            date: formattedDate,
            glucose: vitals.glucose,
            target: 100,
            postMeal: null, // We don't have post-meal data in current model
            appointmentDate: booking.appointment_date,
            unit: vitals.glucose_unit || 'mg/dL'
          });
        }
        
        // Weight
        if (vitals.weight !== null && vitals.weight !== undefined) {
          analytics.weight.push({
            date: formattedDate,
            weight: parseFloat(vitals.weight),
            target: 65.0, // Target weight - could be made dynamic
            appointmentDate: booking.appointment_date,
            unit: vitals.weight_unit || 'kg'
          });
        }
        
        // BMI
        if (vitals.bmi !== null && vitals.bmi !== undefined) {
          analytics.bmi.push({
            date: formattedDate,
            bmi: parseFloat(vitals.bmi),
            normal: 22.5, // Normal BMI reference
            appointmentDate: booking.appointment_date
          });
        }
      }
    });

    return analytics;
  }

  /**
   * Format date for chart display
   * @param {Date} date - Date object
   * @returns {string} Formatted date string
   */
  formatDateForChart(date) {
    const options = { month: 'short', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  }

  /**
   * Format date for period display with year
   * @param {Date} date - Date object
   * @returns {string} Formatted date string with year
   */
  formatDateForPeriod(date) {
    const options = { month: 'short', day: 'numeric', year: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  }

  /**
   * Get analytics summary statistics
   * @param {Object} analyticsData - Processed analytics data
   * @returns {Object} Summary statistics
   */
  getAnalyticsSummary(analyticsData) {
    const summary = {
      temperature: this.getVitalSummary(analyticsData.temperature, 'temp'),
      pulseRate: this.getVitalSummary(analyticsData.pulseRate, 'rate'),
      spo2: this.getVitalSummary(analyticsData.spo2, 'spo2'),
      bloodPressure: {
        systolic: this.getVitalSummary(analyticsData.bloodPressure, 'systolic'),
        diastolic: this.getVitalSummary(analyticsData.bloodPressure, 'diastolic')
      },
      respiratoryRate: this.getVitalSummary(analyticsData.respiratoryRate, 'rate'),
      glucose: this.getVitalSummary(analyticsData.glucose, 'glucose'),
      weight: this.getVitalSummary(analyticsData.weight, 'weight'),
      bmi: this.getVitalSummary(analyticsData.bmi, 'bmi')
    };

    return summary;
  }

  /**
   * Calculate summary statistics for a vital sign
   * @param {Array} data - Array of vital sign measurements
   * @param {string} field - Field name to analyze
   * @returns {Object} Summary statistics
   */
  getVitalSummary(data, field) {
    if (!data || data.length === 0) {
      return { count: 0, average: null, min: null, max: null, latest: null };
    }

    const values = data.map(item => item[field]).filter(val => val !== null && val !== undefined);
    
    if (values.length === 0) {
      return { count: 0, average: null, min: null, max: null, latest: null };
    }

    return {
      count: values.length,
      average: (values.reduce((sum, val) => sum + val, 0) / values.length).toFixed(1),
      min: Math.min(...values),
      max: Math.max(...values),
      latest: data[data.length - 1][field]
    };
  }

  /**
   * Check if patient has sufficient data for analytics
   * @param {Object} analyticsData - Processed analytics data
   * @returns {boolean} True if sufficient data exists
   */
  hasSufficientData(analyticsData) {
    const totalDataPoints = 
      analyticsData.temperature.length +
      analyticsData.pulseRate.length +
      analyticsData.spo2.length +
      analyticsData.bloodPressure.length +
      analyticsData.respiratoryRate.length +
      analyticsData.glucose.length +
      analyticsData.weight.length +
      analyticsData.bmi.length;
    
    return totalDataPoints > 0;
  }

  /**
   * Extract error message from API response
   * @param {Error} error - Error object
   * @returns {string} Error message
   */
  getErrorMessage(error) {
    if (error.response?.data?.detail) {
      if (typeof error.response.data.detail === 'string') {
        return error.response.data.detail;
      }
      if (Array.isArray(error.response.data.detail)) {
        return error.response.data.detail.map(err => err.msg || err).join(', ');
      }
    }
    
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    
    if (error.message) {
      return error.message;
    }
    
    return 'Network error. Please check your connection.';
  }
}

export const patientAnalyticsService = new PatientAnalyticsService();
